import React, {ReactElement, useMemo, useState} from 'react';

import {StorageCropId} from '../../decision/data/storageAshTransport';
import {useTransportCalculator} from '../../decision/transportCalculator';
import {buildSimpleCalculatorTableRow, buildStorageCropIdRow} from '../../util/calculators.util';
import Select from '../Select';
import SimpleCalculatorCard from './SimpleCalculatorCard';
import SimpleCalculatorTable, {SimpleCalculatorTableCell} from './SimpleCalculatorTable';
import ValueWithUnit from './ValueWithUnit';

type Props = {};

const areaHarvestedOptions = [3, 5, 10, 20, 30].map(area => ({
  label: `${area} ha`,
  value: area,
}));

const mcOptions = Array(10)
  .fill(null)
  .map((_, i) => 10 + i * 5)
  .map(mc => ({
    label: `${mc} %`,
    value: mc,
  }));

const TransportCalculator = ({}: Props): ReactElement => {
  const [areaHarvested, setAreaHarvested] = useState<number>();
  const [cropId, setCropId] = useState<StorageCropId>();
  const [mc, setMc] = useState<number>();

  const input = {areaHarvested, cropId, mc};
  const {data} = useTransportCalculator(input);

  const areaHarvestedLabel = areaHarvested !== undefined ? `${areaHarvested} ha` : 'Select';
  const mcLabel = mc !== undefined ? `${mc} %` : 'Select';

  const values = useMemo<SimpleCalculatorTableCell[][]>(
    () => [
      buildSimpleCalculatorTableRow({
        leftContent: 'Area harvested',
        rightContent: (
          <Select
            floating
            variant={'table'}
            buttonContent={areaHarvestedLabel}
            options={areaHarvestedOptions}
            selectedValues={areaHarvested ? [areaHarvested] : []}
            onOptionSelected={value => setAreaHarvested(value)}
            buttonClassName={'rounded-r-2xl'}
          />
        ),
        border: 'both',
        type: 'input',
      }),
      buildStorageCropIdRow({cropId, onSelected: setCropId}),
      buildSimpleCalculatorTableRow({
        leftContent: 'Material form',
        rightContent: <ValueWithUnit value={data?.crop?.materialForm} />,
        border: 'middle',
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Typical yield (ODT)',
        rightContent: <ValueWithUnit value={data?.crop?.driedYield} unit={'tonnes/ha'} />,
        border: 'middle',
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Bulk Density',
        rightContent: <ValueWithUnit value={data?.crop?.bulkDensity} unit={'tonnes/m3'} />,
        border: 'middle',
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Transport vehicle',
        rightContent: <ValueWithUnit value={data?.crop?.vehicle} />,
        border: 'bottom',
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Crop to be Transported (ODT)',
        rightContent: <ValueWithUnit value={data?.cropWeightDried} unit={'tonnes'} />,
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'MC at point of transport',
        rightContent: (
          <Select
            floating
            variant={'table'}
            buttonContent={mcLabel}
            options={mcOptions}
            selectedValues={mc ? [mc] : []}
            onOptionSelected={value => setMc(value)}
            menuClassName={'max-h-[300px]'}
            buttonClassName={'rounded-r-2xl'}
          />
        ),
        border: 'both',
        type: 'input',
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Crop weight to be transported',
        rightContent: <ValueWithUnit value={data?.cropWeightTransported} unit={'tonnes'} />,
      }),
      buildSimpleCalculatorTableRow({
        leftContent: 'Number of Loads',
        rightContent: <ValueWithUnit value={data?.loads} />,
      }),
    ],
    [
      areaHarvestedLabel,
      areaHarvested,
      cropId,
      data?.crop?.materialForm,
      data?.crop?.driedYield,
      data?.crop?.bulkDensity,
      data?.crop?.vehicle,
      data?.cropWeightDried,
      data?.cropWeightTransported,
      data?.loads,
      mcLabel,
      mc,
    ]
  );

  return (
    <SimpleCalculatorCard title={'Transport'}>
      <SimpleCalculatorTable values={values} />
    </SimpleCalculatorCard>
  );
};

export default TransportCalculator;
